import { Injectable, Inject, Optional } from '@angular/core';
import { CookieStorage, CookieOptions } from '../interfaces';
import { NullCookieStorageAdapter } from '../adapters/null-cookie-storage.adapter';
import { COOKIE_STORAGE_ADAPTER } from '../tokens';

@Injectable()
export class CookieService {
  defaultOptions: CookieOptions = {};
  private storageAdapter: CookieStorage;

  constructor(
    @Optional()
    @Inject(COOKIE_STORAGE_ADAPTER)
    adapter?: CookieStorage
  ) {
    this.storageAdapter = adapter || new NullCookieStorageAdapter();
  }

  /**
   * Returns whether a cookie exists.
   *
   * @param name The cookie name
   * @returns TRUE if cookie exists, FALSE otherwise
   */
  has(name: string): boolean {
    return this.storageAdapter.has(name);
  }

  /**
   * Returns the value of given cookie name.
   *
   * @param name The cookie name
   * @returns Raw cookie value
   */
  get(name: string): string | undefined {
    return this.storageAdapter.get(name);
  }

  /**
   * Sets a value for given cookie name.
   *
   * @param name The cookie name
   * @param value Raw value to be stored
   * @param options Cookie options
   */
  set(name: string, value: string, options?: CookieOptions): void {
    this.storageAdapter.set(name, value, options || this.defaultOptions);
  }

  /**
   * Returns the deserialized value of given cookie name.
   *
   * @param name the cookie name
   * @returns Deserialized cookie value
   */
  getObject(name: string): any | undefined {
    const value = this.get(name);

    if (value !== undefined) {
      return CookieService.decode(value);
    }
    return undefined;
  }

  /**
   * Serializes and sets a value for given cookie name.
   *
   * @param name The cookie name
   * @param value Value to be stored
   * @param options Cookie options
   */
  setObject(name: string, value: any, options?: CookieOptions): void {
    this.set(name, CookieService.encode(value), options || this.defaultOptions);
  }

  /**
   * Remove given cookie.
   *
   * @param name Name of the cookie to delete
   * @param options Cookie options
   */
  remove(name: string, options?: CookieOptions): void {
    this.storageAdapter.remove(name, options || this.defaultOptions);
  }

  /**
   * Encodes cookie value.
   *
   * @param data value to be encoded
   * @returns encoded cookie value
   */
  static encode(data: any): string {
    const search = { '+': '-', '/': '_' };
    const encoded = Buffer.from(JSON.stringify(data))
      .toString('base64')
      .replace(/\+|\//gi, matched => search[matched]);

    return encoded.replace(/=+$/, '');
  }

  /**
   * Decodes cookie value.
   *
   * @param data value to be decoded
   * @returns decoded cookie value
   */
  static decode(data: string): any {
    const search = { '-': '+', _: '/' };
    const encoded = data.replace(/\-|\_/gi, matched => search[matched]);
    const padding = new Array((encoded.length % 4) + 1);

    return JSON.parse(Buffer.from(encoded + padding.join('='), 'base64').toString());
  }
}
